// Per-connection CASE POLICY for the navigational features — the engine
// settings that decide whether `Sales` and `sales`, or `DbA.P` and `dba.P`,
// are one thing or two.
//
// Four values live here today: MySQL's `@@lower_case_table_names` (per-kind,
// per-component object keys) and `@@lower_case_file_system` (trigger names —
// the one component the mode does not reach), SQL Server's SERVER collation
// (database identities) and SQL Server's per-DATABASE collation (object +
// schema keys — a `_CI_` instance can hold a `_CS_` database). All are learnt
// by a one-row probe (`mysqlCaseProbe.ts`, `mssqlCollationProbe.ts`); this
// module only holds what was learnt, so the probes share one teardown rule and
// one staleness guard.
//
// ONE STORE PER CONCERN. A value whose staleness question is the one described
// below belongs in `CasePolicyValues`, not in a map of its own beside it —
// even when only a single component reads it, and even when the lifecycle's
// gate ignores it. A second store keyed on the same connection id has to
// re-implement the generation guard and be remembered by every teardown, and
// the first time one of the two is forgotten they disagree about which server
// answered. Which values the GATE requires is a separate question, answered at
// the gate (`connection-lifecycle/navCasePolicy.ts`), not by where a value is
// stored.
//
// GENERATION GUARD. A probe's answer describes the server that was on the
// other end of the connection id WHEN IT WAS ASKED. The app reuses a
// connection id across a disconnect / reconnect, and the next server may have
// a different setting — so `forgetCasePolicy` (called from the navigational
// lifecycle's teardown) bumps the connection's generation, and a probe that
// captured the previous generation is DISCARDED when it lands. Without it a
// late answer would both freeze the wrong keys and suppress the fresh probe
// (a probe skips its query when the value is already known).
//
// FORGOTTEN-AT. The same staleness question reaches values this module did not
// probe: `mysqlCaseProbe.ts` also reads `@@lower_case_table_names` off the
// IntelliSense schema snapshot, which is NOT the navigational layer's to drop
// on a disconnect (it belongs to `mysqlSchemaCache.ts`, is shared with
// completion / hover, and is invalidated on its own schedule). Such a value
// arrives with no generation token, and its `builtAt` cannot stand in for one:
// the snapshot is stamped when its load COMPLETES, so a load of the previous
// server that was still running at the teardown finishes afterwards and stamps
// a moment after it. Comparing timestamps would accept it. So a teardown only
// records THAT it happened, and `casePolicyForgottenAt` answers a number for
// an id that has ever been torn down — after which the snapshot is not
// consulted for that id at all and only a fresh probe answers.

/** One SQL Server database's OBJECT/SCHEMA comparison rule, and HOW it was
 *  learnt.
 *
 *  `measured` is `true` only when the server itself answered the
 *  `N'a' COLLATE CATALOG_DEFAULT = N'A' COLLATE CATALOG_DEFAULT` comparison —
 *  i.e. `caseSensitive` IS `catalog_cs`, the rule SQL Server compares metadata
 *  in. `false` means the answer was inferred from a collation NAME instead:
 *  the fallback shape (`DATABASEPROPERTYEX(DB_NAME(), 'Collation')`, all a
 *  pre-2012 server can say) or `rememberMssqlDatabaseCollation`. That
 *  inference reads the DATA collation, which equals the catalog collation in
 *  every NON-contained database and diverges from it in a contained (or Azure
 *  `CATALOG_COLLATION`) one.
 *
 *  Nothing in production branches on `measured` — the flag exists so a
 *  real-server test can assert that the measurement actually RAN, which the
 *  answer alone cannot show wherever the two shapes agree. */
export type MssqlObjectCaseRule = {
  readonly caseSensitive: boolean;
  readonly measured: boolean;
};

export type CasePolicyValues = {
  /** MySQL `@@lower_case_table_names` — 0 / 1 / 2. */
  readonly mysqlLowerCaseTableNames?: number;
  /** MySQL `@@lower_case_file_system` — whether the data directory sits on a
   *  case-INSENSITIVE file system. It decides exactly one component, a TRIGGER
   *  name (`mysqlCaseProbe.ts`'s `mysqlTriggerNamesCaseSensitive`), and it is
   *  deliberately NOT part of the lifecycle's index-build gate, which keys on
   *  the MODE alone: a server that answers `@@lower_case_table_names` but
   *  rejects the paired shape still gets an index. It is remembered HERE all
   *  the same — see ONE STORE PER CONCERN above — because its staleness rule is
   *  the mode's, to the letter. `undefined` means unknown, and the mode stands
   *  in for it (mode 0 implies a case-sensitive file system). */
  readonly mysqlLowerCaseFileSystem?: boolean;
  /** SQL Server: does the SERVER collation tell `Sales` from `sales`? */
  readonly mssqlCaseSensitiveDatabaseNames?: boolean;
  /** SQL Server: does each DATABASE's collation tell `dbo.V` from `dbo.v`?
   *  Keyed by the database IDENTITY key, since that is what the lifecycle
   *  files an index under. Merged, never replaced — one connection learns one
   *  database at a time — into a prototype-less record, because the keys are
   *  database NAMES (see {@link mergeDatabaseFlags}). */
  readonly mssqlCaseSensitiveObjectNames?: Readonly<Record<string, MssqlObjectCaseRule>>;
};

/** Bumped by every `forgetCasePolicy`. Kept AFTER a teardown on purpose — it
 *  is what makes a late answer from the previous connection identifiable. */
const generations = new Map<string, number>();
const remembered = new Map<string, CasePolicyValues>();
/** When each connection was last torn down (ms). See FORGOTTEN-AT above. */
const forgottenAt = new Map<string, number>();

/** The token a probe captures before its query and hands back to
 *  {@link publishCasePolicy} when the answer lands. */
export function casePolicyGeneration(connectionId: string): number {
  return generations.get(connectionId) ?? 0;
}

export function readCasePolicy(connectionId: string): CasePolicyValues {
  return remembered.get(connectionId) ?? {};
}

/** Store what a probe learnt — unless the connection was torn down (or
 *  reconnected) since `generation` was captured, in which case the answer is
 *  about a server this id no longer points at and is dropped. */
export function publishCasePolicy(
  connectionId: string,
  generation: number,
  values: CasePolicyValues,
): void {
  if (casePolicyGeneration(connectionId) !== generation) return;
  const previous = readCasePolicy(connectionId);
  remembered.set(connectionId, {
    ...previous,
    ...values,
    // Per-database answers accumulate; a second database must not erase the
    // first.
    ...(values.mssqlCaseSensitiveObjectNames
      ? {
          mssqlCaseSensitiveObjectNames: mergeDatabaseFlags(
            previous.mssqlCaseSensitiveObjectNames,
            values.mssqlCaseSensitiveObjectNames,
          ),
        }
      : {}),
  });
}

/** Merge two per-database records into a PROTOTYPE-LESS one. The keys are
 *  database names, i.e. user data — a database really can be called
 *  `constructor` or `toString`, and read off an ordinary object those names
 *  answer with an inherited `Object.prototype` member instead of `undefined`,
 *  which every "has this database been probed?" test then reads as "yes".
 *  `Object.create(null)` removes the inherited answers entirely; readers still
 *  use `Object.hasOwn` so a record built elsewhere is safe too. */
function mergeDatabaseFlags(
  previous: Readonly<Record<string, MssqlObjectCaseRule>> | undefined,
  next: Readonly<Record<string, MssqlObjectCaseRule>>,
): Readonly<Record<string, MssqlObjectCaseRule>> {
  const merged = Object.create(null) as Record<string, MssqlObjectCaseRule>;
  for (const source of [previous, next]) {
    if (!source) continue;
    for (const database of Object.keys(source)) merged[database] = source[database]!;
  }
  return merged;
}

/** Store a value learnt outside a probe (e.g. MySQL's schema snapshot, which
 *  carries `lowerCaseTableNames` of the live connection). */
export function rememberCasePolicy(connectionId: string, values: CasePolicyValues): void {
  publishCasePolicy(connectionId, casePolicyGeneration(connectionId), values);
}

/** When this connection was last torn down — `undefined` while it never has
 *  been. A value that arrives WITHOUT a generation token (a cached schema
 *  snapshot) is only trustworthy for an id that has never been torn down: see
 *  FORGOTTEN-AT above for why a timestamp comparison is not enough. The moment
 *  itself is kept for diagnostics. */
export function casePolicyForgottenAt(connectionId: string): number | undefined {
  return forgottenAt.get(connectionId);
}

/** Drop everything remembered for a connection and invalidate every probe
 *  still in flight for it (disconnect / teardown). */
export function forgetCasePolicy(connectionId: string): void {
  generations.set(connectionId, casePolicyGeneration(connectionId) + 1);
  forgottenAt.set(connectionId, Date.now());
  remembered.delete(connectionId);
}

/** Test-only — wipe the remembered values AND the generations between cases. */
export function __resetCasePolicyStateForTest(): void {
  generations.clear();
  forgottenAt.clear();
  remembered.clear();
}
